import {
  ArrowRightOutline,
  DropdownInput,
  formatNumber,
  modalStyles,
} from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';

import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import usePotOptions from 'app/hooks/usePotOptions';
import CenterSpinner from 'app/components/UI/CenterSpinner';

import usePoints from 'app/hooks/usePoints';

import PotInformation from '../PotInformation/PotInformation';

import {
  TransferPointsFormFields,
  DEFAULT_CREATE_POT_FORM_DATA,
  TransferPointsFormProps,
  TransferPotInfo,
  createSchema,
  PREDEFINED_AMOUNTS,
} from './TransferPointsForm.types';

function TransferPointsForm({
  onClose,
  onSuccess,
  onError,
  companyId,
  originPot,
}: TransferPointsFormProps) {
  // Every pot the points could go to: the company wallet, a pride's, or another campaign's. Never an
  // employee's — the list the hook builds does not carry them.
  const { options, isLoading } = usePotOptions(companyId, originPot.id);

  const currentBalance = originPot.balance;

  const schema = useMemo(() => createSchema(currentBalance), [currentBalance]);

  const methods = useForm<TransferPointsFormFields>({
    resolver: yupResolver(schema) as Resolver<TransferPointsFormFields>,
    mode: 'onChange',
    defaultValues: DEFAULT_CREATE_POT_FORM_DATA,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
  } = methods;

  const amount = watch('amount');

  useEffect(() => {
    if (amount) {
      trigger('amount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, trigger]);

  const { mutate: transferPoints } = usePoints.useTransferPoints({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTransferPoints = (data: TransferPointsFormFields) => {
    const points = parseInt(data.amount || '0', 10);
    const destinationOption = options.find((o) => String(o.value) === String(data.destinationPot));
    const potInfo: TransferPotInfo = {
      fromPotName: originPot.name,
      toPotName: destinationOption?.text || 'pot',
      points,
    };

    setIsSubmitting(true);
    transferPoints(
      {
        companyId,
        fromPotId: originPot.id,
        toPotId: data.destinationPot || '',
        points,
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onSuccess?.(potInfo);
        },
        onError: () => {
          setIsSubmitting(false);
          onError?.(potInfo);
        },
      }
    );
  };

  const handleCancel = () => {
    onClose();
  };

  const handleConfirm = (data: TransferPointsFormFields) => {
    handleTransferPoints(data);
  };

  const renderPredefinedAmounts = useMemo(() => {
    return (
      <HStack w="full" gap="10px">
        {PREDEFINED_AMOUNTS.map((option) => {
          const resolvedValue = option.value === '__all__' ? String(currentBalance) : option.value;
          const isSelected = amount === resolvedValue;
          return (
            <Flex
              key={option.value}
              cursor="pointer"
              w="100px"
              h="40px"
              p="8px 20px"
              bg={isSelected ? 'brand.purple' : 'brand.lightGray2'}
              borderRadius="20px"
              border="1px solid"
              borderColor={isSelected ? 'brand.purple' : 'brand.lightGray1'}
              align="center"
              justify="center"
              color={isSelected ? 'brand.white' : 'brand.dark'}
              _hover={{ bg: 'brand.purple', color: 'brand.white', borderColor: 'brand.purple' }}
              onClick={() =>
                setValue('amount', resolvedValue, { shouldValidate: true, shouldDirty: true })
              }
            >
              {option.label}
            </Flex>
          );
        })}
      </HStack>
    );
  }, [amount, currentBalance, setValue]);

  if (isLoading) {
    return <CenterSpinner />;
  }

  return (
    <FormProvider {...methods}>
      <VStack spacing={6} align="stretch" maxW="100%" w="100%">
        {/* Pot Information */}
        <PotInformation name={originPot.name} currentBalance={currentBalance} />

        <Flex gap="20px" alignItems="flex-start" flexDir="column">
          <FormControl isInvalid={!!errors?.destinationPot?.message} isRequired>
            <FormLabel>Transfer to</FormLabel>
            <Controller
              control={control}
              name="destinationPot"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Select destination"
                  defaultValue={options.find(
                    (destinationPot) => String(destinationPot.value) === String(value)
                  )}
                  options={options}
                  onChange={(option) => onChange(option != null ? String(option) : undefined)}
                  onBlur={onBlur}
                  isSearchable
                  renderText={(option) => {
                    return (
                      <Flex direction="row" flex="1">
                        <Text as="p" textStyle="smallMedium">
                          {option.text}
                        </Text>
                        <>
                          <Spacer />
                          <Flex direction="row" gap="5px" align="center">
                            <Text as="span">{formatNumber(option.points)} points</Text>
                          </Flex>
                        </>
                      </Flex>
                    );
                  }}
                />
              )}
            />
            <FormErrorMessage>{errors?.destinationPot?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors?.amount} isRequired flex={1}>
            <FormLabel>No. of points to transfer</FormLabel>
            <Box position="relative">
              <Controller
                control={control}
                name="amount"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter amount"
                    isInvalid={!!errors.amount}
                    value={value ? parseInt(value, 10).toLocaleString() : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, '');
                      if (raw === '' || /^\d+$/.test(raw)) {
                        onChange(raw || undefined);
                      }
                    }}
                    onBlur={onBlur}
                  />
                )}
              />
            </Box>
            <FormErrorMessage>{errors?.amount?.message as string}</FormErrorMessage>
          </FormControl>

          {renderPredefinedAmounts}
        </Flex>

        <Text color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>

        {/* Footer Buttons */}
        <HStack spacing={3} justify="flex-end" p={modalStyles.baseStyle.footer.p}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            type="submit"
            colorScheme="purple"
            rightIcon={<ArrowRightOutline />}
            isLoading={isSubmitting}
            isDisabled={!isValid || !isDirty}
            onClick={() => {
              handleSubmit(handleConfirm)();
            }}
          >
            Confirm
          </Button>
        </HStack>
      </VStack>
    </FormProvider>
  );
}

export default TransferPointsForm;
